import { limit, orderBy } from 'firebase/firestore'
import { useCollection } from '../../lib/firestore'
import { AdminPage } from './Admin'

/**
 * The audit trail.
 *
 * Read straight from Firestore rather than through a function — the rules make
 * it admin-readable and writable by nobody, so the client can read it directly
 * and there is no path for anything but the Cloud Functions to add to it. An
 * audit log a client can write to isn't one.
 */
interface AuditEntry {
  actorId: string
  actorEmail: string
  action: string
  detail?: Record<string, unknown>
  at?: { toDate: () => Date }
}

const LABELS: Record<string, string> = {
  'users.list': 'Listed all users',
  'users.view': 'Opened an account',
  'users.disable': 'Disabled an account',
  'users.enable': 'Re-enabled an account',
  'users.grantAdmin': 'Granted admin',
  'users.revokeAdmin': 'Revoked admin',
  'stats.view': 'Viewed the overview',
  'doctor.scan': 'Ran a data scan',
  'doctor.fix': 'Applied a data fix',
}

/** Actions that changed something, as opposed to merely looking. */
const WRITES = new Set([
  'users.disable',
  'users.enable',
  'users.grantAdmin',
  'users.revokeAdmin',
  'doctor.fix',
])

export default function AdminAudit() {
  const entries = useCollection<AuditEntry>('adminAudit', [orderBy('at', 'desc'), limit(200)])

  return (
    <AdminPage
      title="Audit log"
      lede="Every admin action against another account, in order. Written by the Cloud Functions only — nothing client-side can add to it or edit it."
    >
      {entries.loading && <p className="t-caption">Loading…</p>}

      {entries.error && (
        <p className="panel panel-pad t-caption" style={{ color: 'var(--destructive)' }}>
          {entries.error.message}
        </p>
      )}

      {!entries.loading && entries.data.length === 0 && (
        <p className="panel panel-pad t-caption">Nothing recorded yet.</p>
      )}

      {entries.data.length > 0 && (
        <div className="panel rows">
          {entries.data.map((entry) => {
            const isWrite = WRITES.has(entry.action)
            return (
              <div key={entry.id} className="row" style={{ padding: '12px 16px', gap: 12 }}>
                <span
                  aria-hidden
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    flex: 'none',
                    background: isWrite ? 'var(--accent)' : 'var(--text-tertiary)',
                  }}
                />
                <span className="grow stack" style={{ gap: 1, minWidth: 0 }}>
                  <span className="truncate" style={{ fontWeight: isWrite ? 600 : 400 }}>
                    {LABELS[entry.action] ?? entry.action}
                  </span>
                  <span className="t-caption truncate">
                    {entry.actorEmail || entry.actorId}
                    {describe(entry.detail)}
                  </span>
                </span>
                <span className="t-spec-caption faint" style={{ whiteSpace: 'nowrap' }}>
                  {entry.at ? stamp(entry.at.toDate()) : '—'}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </AdminPage>
  )
}

function describe(detail?: Record<string, unknown>): string {
  if (!detail) return ''
  const parts: string[] = []
  if (detail.email) parts.push(String(detail.email))
  else if (detail.uid) parts.push(String(detail.uid))
  if (detail.findingId) parts.push(String(detail.findingId))
  if (typeof detail.changed === 'number') parts.push(`${detail.changed} records`)
  if (typeof detail.count === 'number') parts.push(`${detail.count} results`)
  return parts.length ? ` · ${parts.join(' · ')}` : ''
}

function stamp(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date)
}
